import Client from '../Client';
import { requireSuccessfulResult } from '../SuccessfulResult';
import type {
	WatchdogClient,
	WatchdogCreateData,
	WatchdogCreateResult,
	WatchdogDetail,
	WatchdogDetailResult,
	WatchdogUpdateData
} from '../watchdog/Contract';
import type {
	Attachment,
	Cv,
	InterviewEntry,
	PersonalData,
	Reply,
	ReviewEntry,
	TrackedCompany,
	WatchdogItem,
	WatchdogSettings
} from '../../../component/account/Types';

export default class AccountClient implements WatchdogClient {
	#client: Client;
	pathStart: string = '/v1/user';

	constructor(client: Client) {
		this.#client = client;
	}

	// ─── Account ─────────────────────────────────────────────────────────────
	// These currently return mock data so the UI is fully functional without
	// requiring the backend to be wired up yet. Replace each `return …` with
	// the appropriate `this.#client.request(...)` call once the endpoints exist.

	async post(path: string, data: object) {
		return this.#client.post(this.pathStart + path, data);
	}

	async get(path: string, data: object) {
		return this.#client.get(this.pathStart + path, data);
	}

	async put(path: string, data: object) {
		return this.#client.put(this.pathStart + path, data);
	}

	async delete(path: string, data: object) {
		return this.#client.delete(this.pathStart + path, data);
	}

	async getReplyListData(): Promise<{ items: Reply[]; outcomes: Record<string, string> }> {
		const response: any = await this.get('/reply/list', {});
		const result = requireSuccessfulResult(response, 'Reply list request was not successful');
		return {
			items: (result.items ?? []) as Reply[],
			outcomes: (result.enums?.outcomes ?? {}) as Record<string, string>
		};
	}

	async updateReplyOutcome(id: number, outcome: string | null): Promise<void> {
		const response: any = await this.post('/reply/outcome', { id, outcome });
		requireSuccessfulResult(response, 'Reply outcome update was not successful');
	}

	async saveOfferLike(offerId: number, liked: boolean): Promise<boolean> {
		const response: any = await this.post('/offer-like', { offerId, liked });
		return Boolean(requireSuccessfulResult(response, 'Offer like request was not successful').changed);
	}

	async getReviewList(): Promise<{ items: ReviewEntry[]; states: Record<number, string> }> {
		const response: any = await this.get('/review/list', {});
		const result = requireSuccessfulResult(response, 'Review list request was not successful');
		return {
			items: (result.items ?? []) as ReviewEntry[],
			states: (result.enums?.states ?? {}) as Record<number, string>
		};
	}

	async getInterviewList(): Promise<{ items: InterviewEntry[]; states: Record<number, string> }> {
		const response: any = await this.get('/interview/list', {});
		const result = requireSuccessfulResult(response, 'Interview list request was not successful');
		return {
			items: (result.items ?? []) as InterviewEntry[],
			states: (result.enums?.states ?? {}) as Record<number, string>
		};
	}

	async getTrackedList(): Promise<TrackedCompany[]> {
		return Promise.resolve(MOCK.tracked);
	}

	async getCvList(): Promise<Cv[]> {
		return Promise.resolve(MOCK.cv);
	}

	async getAttachmentList(): Promise<{ items: Attachment[]; types: Record<string, string> }> {
		const response: any = await this.get('/attachment', {});
		const result = requireSuccessfulResult(response, 'Attachment list request was not successful');
		return {
			items: (result.items ?? []) as Attachment[],
			types: (result.enums?.types ?? {}) as Record<string, string>
		};
	}

	async renameAttachment(
		id: number,
		name: string,
		successCallback: () => void,
		errorCallback: (error?: string | null) => void
	): Promise<void> {
		await this.put(`/attachment/${id}`, { name }).then(
			(response: any) => {
				const result = response?.result;
				result?.success ? successCallback() : errorCallback(result?.error);
			},
			(response: any) => {
				errorCallback(response?.response?.result?.error);
			}
		);
	}

	async deleteAttachment(
		id: number,
		successCallback: () => void,
		errorCallback: (error?: string | null) => void
	): Promise<void> {
		await this.delete(`/attachment/${id}`, {}).then(
			(response: any) => {
				const result = response?.result;
				result?.success ? successCallback() : errorCallback(result?.error);
			},
			(response: any) => {
				errorCallback(response?.response?.result?.error);
			}
		);
	}

	async getPersonalData(
		successCallback: (result: object) => void,
		errorCallback: (error?: string | null) => void
	): Promise<void> {
		await this.get('/personal-data', {})
			.then((response: any) => {
				const result = response.result;
				result.success ? successCallback(result) : errorCallback(result.errorMessage);
			})
			.catch((response: any) => {
				// výpadek sítě nebo odpověď bez JSON těla nemá response.result — errorCallback se musí zavolat i tak
				errorCallback(response?.response?.result?.error);
			});
	}

	async savePersonalData(
		data: PersonalData,
		successCallback: (result: object) => void,
		errorCallback: (error?: string | null) => void
	): Promise<void> {
		await this.post('/personal-data', {
			fullName: data.fullName,
			phone: data.phone
		})
			.then((response: any) => {
				const result = response.result;
				result.success ? successCallback(result) : errorCallback(result.error);
			})
			.catch((response: any) => {
				// výpadek sítě nebo odpověď bez JSON těla nemá response.result — errorCallback se musí zavolat i tak
				errorCallback(response?.response?.result?.error);
			});
	}

	async changePassword(
		current: string,
		newPassword: string,
		successCallback: (result: object) => void,
		errorCallback: (error?: string | null) => void
	): Promise<void> {
		await this.post('/password', { current, new: newPassword })
			.then((response: any) => {
				const result = response.result;
				result.success ? successCallback(result) : errorCallback(result.errorMessage);
			})
			.catch((response: any) => {
				// výpadek sítě nebo odpověď bez JSON těla nemá response.result — errorCallback se musí zavolat i tak
				errorCallback(response?.response?.result?.error);
			});
	}

	async cancelAccount(successCallback: () => void, errorCallback: (error?: string | null) => void): Promise<void> {
		await this.delete('/cancel-account', {})
			.then((response: any) => {
				const result = response.result;
				result.success ? successCallback() : errorCallback(result.errorMessage);
			})
			.catch((response: any) => {
				// výpadek sítě nebo odpověď bez JSON těla nemá response.result — errorCallback se musí zavolat i tak
				errorCallback(response?.response?.result?.error);
			});
	}

	async getWatchdogSettings(): Promise<WatchdogSettings> {
		const response: any = await this.get('/watchdog', {});
		const result = requireSuccessfulResult(response, 'Watchdog settings request was not successful');
		return {
			job: (result.job ?? []) as WatchdogItem[],
			brigade: (result.brigade ?? []) as WatchdogItem[],
			eveningSummary: Boolean(result.eveningSummary)
		};
	}

	async getWatchdogDetail(pid: string): Promise<WatchdogDetailResult> {
		const response: any = await this.get('/watchdog-detail', { pid });
		const result = response?.result;
		if (result?.success) {
			return result.detail as WatchdogDetail;
		}

		// error typu `cancelled` = watchdog patří uživateli, ale už byl zrušený
		return Array.isArray(result?.errors) && result.errors.some((error: any) => error?.type === 'cancelled')
			? 'cancelled'
			: null;
	}

	async updateWatchdog(pid: string, data: WatchdogUpdateData): Promise<boolean> {
		// pid jde v query (zrcadlo GET detailu), tělo nese jen editovaná data
		const response: any = await this.put(`/watchdog-detail?${new URLSearchParams({ pid })}`, data);
		return Boolean(response?.result?.success);
	}

	async createWatchdog(data: WatchdogCreateData): Promise<WatchdogCreateResult> {
		const response: any = await this.post('/watchdog', data);
		const result = response?.result;
		if (result?.success) {
			return { success: true };
		}

		// error typu `limit` = dosažený limit počtu upozornění, api k němu posílá hlášku pro uživatele
		const limitReached =
			Array.isArray(result?.errors) && result.errors.some((error: any) => error?.type === 'limit');
		return { success: false, message: limitReached ? String(result.message) : null };
	}

	async setEveningSummary(on: boolean): Promise<boolean> {
		const response: any = await this.put('/watchdog-evening-summary', { on });
		// bez kontroly success by selhání splynulo s validní hodnotou false (souhrn vypnutý)
		const result = requireSuccessfulResult(response, 'Evening summary update was not successful');

		return Boolean(result.eveningSummary);
	}

	async deleteWatchdog(id: number): Promise<boolean> {
		const response: any = await this.delete('/watchdog', { id });
		return Boolean(response?.result?.success);
	}

	async deleteAllWatchdogs(): Promise<boolean> {
		const response: any = await this.delete('/watchdog', { all: true });
		return Boolean(response?.result?.success);
	}
}

// ─── Mock data ──────────────────────────────────────────────────────────────
const MOCK = {
	tracked: [
		{
			id: 1,
			name: 'PRANTL Masný průmysl s.r.o.',
			industry: 'Potravinářství',
			location: 'Žirovnice',
			positions: 3,
			rating: 4.2,
			reviews: 18
		},
		{
			id: 2,
			name: 'Coweo Technologies s.r.o.',
			industry: 'IT a telekomunikace',
			location: 'Praha',
			positions: 8,
			rating: 3.8,
			reviews: 31
		},
		{
			id: 3,
			name: 'Triangle Recruitment CZ s.r.o.',
			industry: 'Personalistika',
			location: 'Písek',
			positions: 5,
			rating: 4.5,
			reviews: 24
		},
		{
			id: 4,
			name: 'Škoda Auto a.s.',
			industry: 'Automobilový průmysl',
			location: 'Mladá Boleslav',
			positions: 42,
			rating: 4,
			reviews: 156
		}
	] as TrackedCompany[],

	cv: [
		{ id: 1, name: 'Životopis - Jan Novák', created: '23. února 2026', privacy: 'private' },
		{ id: 2, name: 'Životopis - Jan Novák – IT pozice', created: '10. ledna 2026', privacy: 'public' }
	] as Cv[]
};
